import { useState } from "react";

/**
 * Interactive execution-fee calculator: slide a portfolio value and see the
 * one-off fee, with the 10% rate, the £999 floor and the £20,000 cap all
 * applied. Pure state, no animation, so it is reduced-motion safe by nature.
 * Keeps the marketing copy and the arithmetic in one place so they cannot drift.
 */

const RATE = 0.1;
const MIN_FEE = 999;
const MAX_FEE = 20000;
const CAP_AT = MAX_FEE / RATE; // recovered value at which the cap is reached

function gbp(n: number): string {
  return "£" + Math.round(n).toLocaleString("en-GB");
}

export default function FeeCalculator() {
  const [value, setValue] = useState(500000);

  const raw = value * RATE;
  const fee = Math.min(MAX_FEE, Math.max(MIN_FEE, raw));
  const capped = raw >= MAX_FEE;
  const floored = raw <= MIN_FEE;
  const effectivePct = value > 0 ? (fee / value) * 100 : 0;

  return (
    <div className="rounded-xl border border-slate-700/50 bg-slate-900/50 p-6" data-testid="fee-calculator">
      <p className="text-sm text-slate-300 mb-4">Try it with your own number:</p>

      <label htmlFor="fee-calc" className="block text-sm text-slate-400 mb-2">
        If your family recovers{" "}
        <span className="text-white font-semibold" data-testid="fee-calc-value">{gbp(value)}</span>
      </label>
      <input
        id="fee-calc"
        type="range"
        min={10000}
        max={2000000}
        step={10000}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="w-full accent-teal-500 cursor-pointer"
        aria-label="Amount your family recovers"
      />
      <div className="flex justify-between text-xs text-slate-500 mt-1">
        <span>£10k</span>
        <span>£2m</span>
      </div>

      <div className="mt-6 flex items-baseline justify-between border-t border-slate-700/50 pt-4">
        <span className="text-slate-400 text-sm">The one-off execution fee</span>
        <span className="text-3xl font-bold text-teal-400" data-testid="fee-calc-result">{gbp(fee)}</span>
      </div>

      <p className="text-slate-500 text-sm mt-3 leading-relaxed">
        {capped
          ? `That is the £20,000 cap, about ${effectivePct.toFixed(1)}% of the total. Once a recovery reaches ${gbp(CAP_AT)}, the fee never rises further, however large the estate.`
          : floored
          ? "That is the £999 minimum, which covers a small recovery."
          : "That is 10% of the amount recovered, a one-off charge paid from the estate."}{" "}
        Many families earmark a whole-of-life policy to cover it.
      </p>
    </div>
  );
}
